// apps/web/src/contexts/ThemeContext.tsx

import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { createTheme as createMuiTheme, ThemeProvider as MuiThemeProvider, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { palette, darkPalette } from '../theme/palette';

// Extend MUI theme to include venue palette
declare module '@mui/material/styles' {
  interface Palette {
    venue: {
      gaming: string;
      snooker: string;
      kids: string;
    };
  }
  interface PaletteOptions {
    venue?: {
      gaming: string;
      snooker: string;
      kids: string;
    };
  }
}

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'lov_theme_mode';

/**
 * Get initial theme from localStorage or system preference
 * Handles errors gracefully (e.g., private browsing, storage quota exceeded)
 */
function getInitialTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';

  // Check localStorage first (with error handling)
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage unavailable (private browsing, quota exceeded, etc.)
    // Fall through to system preference
  }

  // Check system preference
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch {
    // matchMedia unavailable in some environments
  }

  return 'light';
}

/**
 * Theme context provider with dark mode support
 *
 * Features:
 * - localStorage persistence
 * - System preference detection
 * - Document class updates for Tailwind dark: prefix
 * - MUI theme switching based on mode
 */
export function ThemeContextProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialTheme);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't manually set a preference
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          setMode(e.matches ? 'dark' : 'light');
        }
      } catch {
        // localStorage unavailable - skip auto-switching
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Legacy browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Persist theme preference and update document class
  useEffect(() => {
    // Persist to localStorage (with error handling)
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable - silently fail, theme still works in-memory
    }

    // Update document class for Tailwind dark: prefix
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  // Create MUI theme based on mode
  const theme = useMemo(() => {
    const activePalette = mode === 'dark' ? darkPalette : palette;

    return createMuiTheme({
      palette: {
        ...activePalette,
        // Venue colors come from activePalette (light or dark)
        // Light mode: darker colors for WCAG AA contrast
        // Dark mode: vibrant colors for visual appeal
      },

      typography: {
        fontFamily: '"IBM Plex Sans", "SF Pro Display", system-ui, -apple-system, sans-serif',
        h1: {
          fontFamily: '"IBM Plex Mono", "JetBrains Mono", "SF Mono", monospace',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: activePalette.text.primary,
        },
        h2: {
          fontFamily: '"IBM Plex Mono", "JetBrains Mono", "SF Mono", monospace',
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: activePalette.text.primary,
        },
        h3: {
          fontFamily: '"IBM Plex Mono", "JetBrains Mono", "SF Mono", monospace',
          fontWeight: 500,
          letterSpacing: '-0.01em',
          color: activePalette.text.primary,
        },
        button: {
          textTransform: 'none',
          fontWeight: 500,
          fontFamily: '"IBM Plex Mono", "JetBrains Mono", monospace',
          letterSpacing: '0.02em',
        },
      },

      shape: {
        borderRadius: 12,
      },

      shadows: [
        'none',
        '0 1px 2px rgba(0,0,0,0.05)',
        '0 1px 3px rgba(0,0,0,0.08)',
        '0 4px 6px rgba(0,0,0,0.07)',
        '0 4px 8px rgba(0,0,0,0.08)',
        '0 6px 12px rgba(0,0,0,0.08)',
        '0 8px 16px rgba(0,0,0,0.08)',
        '0 10px 20px rgba(0,0,0,0.08)',
        '0 12px 24px rgba(0,0,0,0.1)',
        '0 14px 28px rgba(0,0,0,0.1)',
        '0 16px 32px rgba(0,0,0,0.1)',
        '0 18px 36px rgba(0,0,0,0.1)',
        '0 20px 40px rgba(0,0,0,0.12)',
        '0 22px 44px rgba(0,0,0,0.12)',
        '0 24px 48px rgba(0,0,0,0.12)',
        '0 26px 52px rgba(0,0,0,0.12)',
        '0 28px 56px rgba(0,0,0,0.14)',
        '0 30px 60px rgba(0,0,0,0.14)',
        '0 32px 64px rgba(0,0,0,0.14)',
        '0 34px 68px rgba(0,0,0,0.14)',
        '0 36px 72px rgba(0,0,0,0.16)',
        '0 38px 76px rgba(0,0,0,0.16)',
        '0 40px 80px rgba(0,0,0,0.16)',
        '0 42px 84px rgba(0,0,0,0.16)',
        '0 44px 88px rgba(0,0,0,0.18)',
      ],

      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: {
              scrollBehavior: 'smooth',
            },
            body: {
              backgroundColor: activePalette.background.default,
              minHeight: '100vh',
            },
            '::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '::-webkit-scrollbar-track': {
              background: activePalette.background.default,
            },
            '::-webkit-scrollbar-thumb': {
              background: alpha(activePalette.text.secondary, 0.2),
              borderRadius: '4px',
              '&:hover': {
                background: alpha(activePalette.text.secondary, 0.3),
              },
            },
            '::selection': {
              backgroundColor: alpha(activePalette.secondary.main, 0.2),
              color: activePalette.text.primary,
            },
            '*:focus-visible': {
              outline: `3px solid ${activePalette.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '4px',
            },
          },
        },
        MuiButton: {
          defaultProps: {
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              borderRadius: 10,
              padding: '12px 24px',
              fontSize: '0.9375rem',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            contained: {
              backgroundColor: activePalette.primary.main,
              color: activePalette.primary.contrastText,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: activePalette.primary.dark,
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
            },
          },
        },
      },
    });
  }, [mode]);

  const contextValue: ThemeContextValue = {
    mode,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme mode and toggle function
 *
 * Usage:
 * const { mode, toggleTheme } = useTheme();
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
}
